/** English strings for the commerce area. The Vietnamese file must have exactly the same keys. */
export const commerce = {
  crumb: 'Business',
  filter: { customer: 'Customer: {{name}}', customerUnknown: 'One customer only' },
  pager: { newer: 'Newer', older: 'Older', page: 'Page {{n}}' },
  common: {
    status: 'Status',
    customer: 'Customer',
    order: 'Order',
    domain: 'Domain',
    product: 'Product',
    plan: 'Service plan',
    operation: 'Operation',
    amount: 'Amount',
    subtotal: 'Subtotal',
    tax: 'Tax',
    total: 'Total',
    created: 'Created',
    updated: 'Updated',
    issued: 'Issued',
    posted: 'Posted',
    currency: 'Currency',
    purpose: 'Purpose',
    provider: 'Provider',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    platform: 'Platform',
    years: '{{count}} yr',
    yearsLong_one: '{{count}} year',
    yearsLong_other: '{{count}} years',
    premium: 'Premium',
    expires: 'expires',
    reversed: 'reversed',
    debitShort: 'Dr',
    creditShort: 'Cr',
    debit: 'Debit',
    credit: 'Credit',
    entry: 'Entry',
    type: 'Type',
    description: 'Description',
    resource: 'For',
    postings: 'Postings',
    account: 'Account',
    noJournal: 'No journal entries',
    rawAmount: 'Exact amount: {{value}}',
    noReference: 'No reference yet',
    copyId: 'Identifier',
  },
  quotes: {
    title: 'Quotes',
    subtitle: 'What a customer was quoted for a domain or service, and the price that was fixed at that moment.',
    domainFilter: 'Domain',
    domainPlaceholder: 'Name prefix',
    loadError: 'Could not load quotes',
    empty: 'No quotes match',
    col: { domain: 'Product', operation: 'Operation', customer: 'Customer', subtotal: 'Subtotal', tax: 'Tax', total: 'Total', state: 'State', created: 'Created' },
    state: { ORDERED: 'Ordered', EXPIRED: 'Expired', OPEN: 'Open' },
  },
  quote: {
    title: 'Quote',
    titleFor: 'Quote for {{domain}}',
    loadError: 'Could not load the quote',
    field: { id: 'Quote identifier', state: 'State', tld: 'TLD', premium: 'Premium', expires: 'Valid until' },
    breakdown: 'How this price was built',
    bd: {
      registryCost: 'Registry cost',
      converted: 'In billing currency',
      rate: '1 {{base}} = {{rate}}',
      priceBook: 'Price book',
      rule: 'Price rule',
      base: 'Price before discount',
      promotion: 'Promotion',
      tax: 'Tax {{code}} ({{rate}} %)',
      version: 'version {{n}}',
      premiumCost: 'Premium name cost',
      noPromotion: 'None applied',
    },
    immutable: 'A quote never changes. It keeps every price component and the price-book, exchange-rate and tax versions that were in force, so the amount charged can always be explained.',
  },
  orders: {
    title: 'Orders',
    subtitle: 'What each customer ordered, what it cost and how far fulfilment got.',
    loadError: 'Could not load orders',
    empty: 'No orders match',
    col: { order: 'Products ordered', customer: 'Customer', status: 'Status', items: 'Products', total: 'Total', placed: 'Placed', updated: 'Updated' },
  },
  order: {
    title: 'Order',
    titleFor: 'Order for {{domain}}',
    loadError: 'Could not load the order',
    field: { placed: 'Placed', id: 'Order identifier' },
    stat: { status: 'Order status', total: 'Order total', items: 'Products', fulfilled: '{{count}} fulfilled' },
    tab: { items: 'Products ({{count}})', ledger: 'Ledger entries ({{count}})', documents: 'Payments, invoices and refunds ({{count}})' },
    itemCol: { domain: 'Product', operation: 'Operation', status: 'Status', amount: 'Amount', hold: 'Funds held', quote: 'Quote', workflow: 'Workflow' },
    viewQuote: 'Price quote',
    viewWorkflow: 'Fulfilment',
    ledgerEmpty: 'Nothing posted yet: funds are only held until the product is delivered.',
    noDocuments: 'None. Orders are paid from the customer wallet; invoices are issued after fulfilment.',
    payments: 'Payments',
    invoices: 'Invoices and credit notes',
    refunds: 'Refunds',
    issuedAt: 'issued {{at}}',
  },
  billing: {
    title: 'Billing',
    subtitle: 'Customer balances, the funds reserved on them, and what has been paid, refunded and invoiced.',
    limitTitle: 'No payment or e-invoice provider is connected',
    limit: 'Payments only run through an internal test provider, and invoices stay internal documents: nothing here has moved real money or been filed with the tax authority.',
    loadError: 'Could not load the billing overview',
    wallets: 'Customer wallets ({{currency}})',
    walletCount_one: '{{count}} wallet',
    walletCount_other: '{{count}} wallets',
    availableLabel: 'Available balance',
    heldLabel: 'Held',
    walletLink: 'Wallet accounts',
    noWallets: 'No customer wallet accounts yet.',
    openHolds: 'Open holds',
    openHoldsHint: 'Funds reserved for orders that are still being fulfilled',
    noOpenHolds: 'No open holds',
    failedPayments: 'Failed payments',
    unknownPayments: '{{count}} awaiting reconciliation',
    noFailedPayments: 'No failed payments',
    imbalance: 'Ledger imbalance',
    balanced: 'Balanced',
    imbalanced: 'Imbalanced',
    imbalanceHint: 'Unbalanced journal entries (must be 0)',
    ledgerLink: 'Wallet / Ledger',
    payments: 'Payments',
    holds: 'Balance holds',
    refunds: 'Refunds',
    invoices: 'Invoices and credit notes',
    noneRecorded: 'None recorded.',
    noneIssued: 'None issued.',
    col: { status: 'Status', count: 'Count', amount: 'Amount', document: 'Document' },
    holdsList: 'Balance holds',
    holdsLoadError: 'Could not load holds',
    holdsEmpty: 'No holds match',
    holdCol: { hold: 'Held for', account: 'Account', purpose: 'Purpose', status: 'Status', amount: 'Amount', created: 'Created', closes: 'Expires / closed' },
    latestEntries: 'Latest journal entries',
    latestPayments: 'Latest payments',
    noPayments: 'No payments yet.',
    viewAll: 'View all',
  },
  hold: {
    title: 'Balance hold',
    titleFor: 'Funds held for {{domain}}',
    loadError: 'Could not load the hold',
    explain: 'An active hold sets funds aside in the customer wallet: the money is still theirs but cannot be spent twice. It is captured when the name is registered, released when the attempt fails, and expires on its own if nothing happens.',
    field: { id: 'Hold identifier', account: 'Wallet account', capture: 'Capture entry', expires: 'Expires', closed: 'Closed', reserved: 'Reserved for' },
    stat: { status: 'Hold status', amount: 'Amount held', available: 'Available after this hold' },
    item: 'The name these funds are for',
    itemField: { status: 'Fulfilment status', quote: 'Quote', amount: 'Price' },
    noItem: 'These funds are not reserved for an order line.',
    entries: 'Journal entries for this hold',
    noEntries: 'Nothing posted yet: money only moves when the hold is captured.',
    noAccount: 'The wallet account is no longer available.',
  },
  ledger: {
    title: 'Wallet / Ledger',
    subtitle: 'Every movement of money, in double entry. Corrections are posted as reversals, never as edits.',
    view: 'Ledger view',
    tab: { accounts: 'Accounts', journal: 'Journal' },
    trialBalance: 'Trial balance',
    trialLoadError: 'Could not load the trial balance',
    balancedToZero: 'Balanced to zero',
    imbalancedCount_one: 'Imbalanced: {{count}} entry',
    imbalancedCount_other: 'Imbalanced: {{count}} entries',
    trialHint: 'Every entry is checked when it is posted — debits equal credits in each currency — and postings can never be edited afterwards.',
    entriesPosted: 'Journal entries posted',
    imbalancedEntries: 'Unbalanced entries',
    mustBeZero: 'Must be 0',
    currencies: 'Currencies',
    noAccounts: 'No ledger accounts yet.',
    trialCol: { currency: 'Currency', accounts: 'Accounts', postings: 'Postings', debits: 'Total debits', credits: 'Total credits', difference: 'Debits − credits' },
    codeFilter: 'Code',
    codePlaceholder: 'Prefix, e.g. customer:',
    owner: 'Owner',
    type: 'Type',
    currency: 'Currency',
    accountsLoadError: 'Could not load ledger accounts',
    accountsEmpty: 'No accounts match',
    accountCol: { account: 'Account', type: 'Type', owner: 'Owner', debits: 'Debits', credits: 'Credits', balance: 'Balance', held: 'Held', available: 'Available', status: 'Status' },
    balanceNote: "Balance is on the account's normal side: debits − credits for assets and expenses, credits − debits otherwise.",
    entryType: 'Entry type',
    resource: 'For',
    journalLoadError: 'Could not load journal entries',
    journalEmpty: 'No journal entries match',
  },
  ledgerAccount: {
    title: 'Ledger account',
    crumb: 'Account',
    loadError: 'Could not load the account',
    field: { code: 'Account code', negative: 'Negative balance', allowed: 'Allowed', rejected: 'Rejected', opened: 'Opened', debits: 'Total debits', credits: 'Total credits', postings: 'Postings' },
    stat: { balance: 'Balance', held: 'Held', available: 'Available balance' },
    activeHolds: 'Active holds',
    postings: 'Postings (latest 200)',
    noPostings: 'No postings yet.',
    allEntries: 'All journal entries',
  },
  journal: {
    title: 'Journal entry',
    loadError: 'Could not load the journal entry',
    field: { id: 'Entry identifier', entryType: 'Entry type', effective: 'Effective', actor: 'Posted by', correlation: 'Correlation', reverses: 'Corrects', reversedBy: 'Corrected by', approval: 'Approval', request: 'Request' },
    balanced: 'Debits = credits',
    unbalanced: 'Unbalanced',
    total: 'Total {{currency}}',
    immutable: 'A journal entry is never edited. A correction is posted as a reversal that points back at the original.',
  },
  payments: {
    title: 'Payments',
    subtitle: 'Payment requests by bank transfer with a QR code (wallet top-ups and orders), whether the money arrived, and the refunds going back out.',
    purposeFilter: 'Purpose',
    loadError: 'Could not load payment requests',
    empty: 'No payment requests match',
    col: {
      payment: 'Reference',
      code: 'Transfer content',
      customer: 'Customer',
      purpose: 'Purpose',
      status: 'Status',
      amount: 'Amount',
      settled: 'Credited',
      expires: 'Lapses',
      created: 'Created',
    },
    bankTransactions: 'Bank transactions',
    attention: 'Sessions to handle',
    attentionHint: 'Requests with a gateway attempt whose result is unknown, that is stuck past its expiry, whose money does not fit it, or whose refund to the source has waited at the gateway for over a day',
    refunds: 'Refunds',
    refundsLoadError: 'Could not load refunds',
    refundsEmpty: 'No refunds match',
    refundCol: { refund: 'Refunded name', customer: 'Customer', order: 'Order', reason: 'Reason', status: 'Status', amount: 'Amount', entry: 'Ledger entry', created: 'Created' },
    refundNoName: 'Refund',
  },
  duplicateRef: {
    found_one: 'This transaction code was recorded once before. Check it so the money is not counted twice:',
    found_other: 'This transaction code was recorded {{count}} times before. Check it so the money is not counted twice:',
    kind: { BANK_TRANSACTION: 'Bank transaction', WALLET_ADJUSTMENT: 'Balance adjustment' },
    acknowledge: 'I checked: this is different money, record it anyway.',
  },
  payment: {
    title: 'Payment request',
    titleFor: 'Payment {{code}}',
    loadError: 'Could not load the payment request',
    notRequest: 'This payment was not made by a bank-transfer QR code (an earlier kind of payment): only its record is shown.',
    field: {
      id: 'Identifier',
      code: 'Transfer content',
      reference: 'Reference',
      method: 'Receiving account',
      provider: 'Method',
      expires: 'Lapses',
      settled: 'Credited to the wallet',
      settlement: 'Settlement entry',
      orderStatus: 'Order status',
      bankTransaction: 'Bank transaction',
      payLink: 'Payment link',
    },
    qr: {
      title: 'QR code for the customer',
      hint: 'The customer scans it in a banking app: account, amount and transfer content are filled in. The transfer content must stay exactly {{code}}.',
      closed: 'No code to show: the request is no longer open.',
      payload: 'QR payload (EMVCo)',
    },
    tab: { transactions: 'Bank transactions ({{count}})', sessions: 'Gateway sessions ({{count}})', approvals: 'Approvals ({{count}})', refunds: 'Refunds ({{count}})', sourceRefunds: 'Refunds to the source ({{count}})' },
    noTransactions: 'No bank transaction names this request yet.',
    noSessions: 'The customer has not opened a payment gateway from this request\'s link.',
    sessionsHint: 'Each time the customer opens a gateway from the payment link starts one session. Money the gateway reports always reaches the customer\'s wallet, even for a session that expired or was cancelled.',
    sessionCol: { status: 'Status', gateway: 'Gateway · method', providerRef: 'Gateway transaction no.', providerCode: 'Result code', created: 'Started', expires: 'Expires', money: 'Money received' },
    session: {
      sync: 'Query status',
      onGatewayPage: 'Chosen on the gateway page',
      checkedAt: 'Last queried {{at}}',
      notCaptured: 'Money arrived while the session is not captured: check the transaction.',
      amountDiffers: 'The gateway reported {{amount}}, not the session\'s amount.',
    },
    refund: {
      action: 'Refund to the source',
      title: 'Refund to the card or account that paid',
      description: "The amount is taken from the customer's wallet and the gateway is asked to pay it back to the card or account that paid. If the gateway cannot refund it, the money returns to the wallet.",
      amount: 'Amount to refund (VND)',
      amountHint: 'Whole đồng, at most {{max}} (what the gateway took less what was refunded).',
      amountInvalid: 'Enter a whole number of đồng above zero.',
      threshold: 'At or above {{threshold}} a second person approves first.',
      submit: 'Refund',
      done: 'The wallet was debited and the gateway asked to refund. The status updates once the gateway is done.',
      requested: 'An approval was asked for. Once another person approves it, refund again with the same amount.',
      insufficient: "The customer's wallet does not hold enough: {{available}} available.",
    },
    resolve: {
      action: 'Record the result',
      title: "Record the gateway's result of the refund",
      description: "The gateway has not reported this refund. Check it in the gateway's merchant admin, then record it: the gateway refunded the customer's card or account, or it did not (the money returns to the customer's wallet).",
      status: 'Result at the gateway',
      completed: 'The gateway refunded the customer',
      failed: 'The gateway did not refund: back to the wallet',
      threshold: 'Returning {{threshold}} or more to the wallet needs a second person to approve first.',
      submit: 'Record',
      done: 'The refund result was recorded.',
      requested: 'An approval was asked for. Once another person approves it, record this result again.',
    },
    refundsHint: "Refunds to the card or account the customer paid with through a gateway. The money left the wallet; a refund that fails returns to it.",
    noSourceRefunds: 'Nothing was refunded to the source for this request.',
    refundCol: { status: 'Status', amount: 'Amount', reason: 'Reason', providerRef: "Gateway's refund no.", providerCode: 'Result code', entry: 'Entry', created: 'Created', checked: 'Last queried' },
    noApprovals: 'No approval was asked for.',
    approvalCol: { status: 'Status', amount: 'Amount', requestedBy: 'Asked by', decidedBy: 'Decided by', requestedAt: 'Asked at' },
    noRefunds: 'No refunds for this payment.',
    confirm: {
      action: 'Confirm transfer received',
      title: 'Confirm a transfer received',
      description: "You saw this request's transfer on the bank statement and no bank feed reported it. The amount is credited to the customer's wallet.",
      amount: 'Amount received (VND)',
      amountHint: 'Whole đồng, as on the statement.',
      amountInvalid: 'Enter a whole number of đồng above zero.',
      reference: 'Bank reference',
      referenceHint: "Required. The statement line's reference, e.g. FT26270001.",
      threshold: 'At or above {{threshold}} a second person approves first.',
      submit: 'Confirm',
      done: 'The transfer was recorded and credited to the wallet.',
      requested: 'An approval was asked for. Once another person approves it (Approvals), confirm again with the same amount.',
    },
    cancel: {
      action: 'Cancel request',
      title: 'Cancel the payment request',
      description: 'The code stops being valid. An order waiting for it is cancelled too.',
      submit: 'Cancel request',
      done: 'The request was cancelled.',
      orderCancelled: 'The request was cancelled, and the order waiting for it too.',
    },
  },
  bankTxs: {
    title: 'Bank transactions',
    subtitle: 'Every transaction the bank feeds (TinoPay, SePay, Casso) report on the receiving accounts and what became of it: matched to a payment request, waiting for staff, or set aside.',
    feedsTitle: 'Bank feeds',
    feedOn: 'On',
    feedOff: 'Off',
    feedLast: 'Last transaction {{when}}',
    feedNever: 'No transaction yet',
    webhook: 'Webhook address',
    noFeeds: 'No bank feed is set up: transfers are not recognised automatically. Set up SePay, Casso or TinoPay in Settings › Modules › Payments.',
    qr: {
      OK: 'Account shown on QR codes: {{connection}}',
      settingsLink: 'Settings › Modules › Payments',
      NO_ACCOUNT: {
        title: 'No receiving account is switched on',
        body: 'Customers cannot pay by transfer until a switched-on connection has a receiving account: pick “Bank of the account” and enter “Account holder” for that connection in',
      },
      AMBIGUOUS: {
        title: 'No account chosen for QR codes',
        body: 'Several switched-on connections have a receiving account ({{connections}}), so no transfer QR code can be made. Pick one under “Connection shown on transfer QR codes” in the settings of the whole area,',
      },
      NOT_USABLE: {
        title: 'The connection for QR codes cannot be used',
        body: 'Connection {{connections}}, picked under “Connection shown on transfer QR codes”, is off, gone, misspelt or has no receiving account, so no transfer QR code can be made. Switch it on, enter its receiving account or pick another one in',
      },
    },
    stat: { unmatched: 'Not matched', review: 'Waiting for review', hint: 'Assign, credit or set aside each one.' },
    statusFilter: 'Status',
    sourceFilter: 'Feed',
    search: 'Search',
    searchPlaceholder: 'Content, code, reference, amount',
    open: 'To handle (not matched or for review)',
    loadError: 'Could not load bank transactions',
    empty: 'No bank transactions match',
    col: { received: 'Received', source: 'Feed', amount: 'Amount', content: 'Transfer content', status: 'Status', request: 'Payment request', customer: 'Customer' },
  },
  bankTx: {
    title: 'Bank transaction',
    titleFor: 'Transaction {{reference}}',
    loadError: 'Could not load the bank transaction',
    field: {
      id: 'Identifier',
      source: 'Feed',
      externalId: "Feed's transaction id",
      direction: 'Direction',
      account: 'Account',
      content: 'Transfer content',
      code: 'Code found',
      reference: 'Bank reference',
      counterparty: 'From',
      booked: 'Booked by the bank',
      received: 'Received',
      request: 'Payment request',
      resolvedBy: 'Handled by',
      resolvedAt: 'Handled at',
      note: 'Note',
    },
    payload: 'What the feed sent',
    suggestions: 'Open requests of the same amount',
    noSuggestions: 'No open request has this amount.',
    useSuggestion: 'Assign to this request',
    assign: {
      action: 'Assign to a request',
      title: 'Assign to a payment request',
      description: 'The transaction pays the request: settled when the request is open, in time and the amount is exact; otherwise it waits for review with the reason.',
      code: 'Transfer content code',
      codeHint: "The request's code, e.g. TINO7K3QX9AB.",
      codeInvalid: 'Not a transfer content code.',
      submit: 'Assign',
    },
    credit: {
      action: 'Credit to a wallet',
      title: 'Credit to a wallet',
      description: 'The whole amount is credited to a wallet: to the request it named while that is open, otherwise as a top-up of the customer.',
      customer: 'Customer identifier (optional)',
      customerHint: 'Empty: the customer of the request it named.',
      submit: 'Credit',
    },
    dismiss: {
      action: 'Set aside',
      title: 'Set the transaction aside',
      description: "Not a customer's payment (e.g. a transfer between the company's own accounts). A refund to the sender is made at the bank.",
      submit: 'Set aside',
    },
    settlement: {
      action: 'Gateway settlement',
      title: 'Settlement from a payment gateway',
      description: "The gateway paying the money it took from customers into the company's account. Each customer's wallet was credited when the gateway reported their payment, so this is only recorded and credits no wallet.",
      module: 'Payment gateway',
      submit: 'Record',
    },
    result: { SETTLED: 'Settled.', REVIEW: 'It waits for review: {{reason}}.', IGNORED: 'Set aside.', UNMATCHED: 'Not matched.' },
  },
  qr: {
    topUp: {
      action: 'Top-up QR',
      title: 'QR code to top up the wallet',
      description: 'A code for {{customer}} to transfer to the receiving account; the money is credited to the wallet when the bank feed reports it.',
      amount: 'Amount (VND)',
      amountHint: 'Whole đồng.',
      amountInvalid: 'Enter a whole number of đồng above zero.',
      submit: 'Make the code',
    },
    order: {
      action: 'Payment QR',
      title: 'QR code to pay the order',
      description: 'A code for what the wallet does not cover; the order goes on as soon as the transfer arrives.',
      submit: 'Make the code',
      funded: 'The wallet covers the order: its funds are held now, nothing to transfer.',
      notAwaiting: 'This order is not waiting for a transfer.',
    },
    invoice: {
      action: 'Payment QR',
      title: 'QR code for this invoice',
      description: "A transfer code for the invoice's order while that waits for payment.",
      notAwaiting: "The invoice's order is already paid. You can make a top-up code of the invoice total instead.",
      topUp: 'Top-up code of {{amount}}',
    },
    shown: {
      account: 'Account number',
      bank: 'Bank',
      holder: 'Account holder',
      amount: 'Amount',
      content: 'Transfer content',
      expires: 'Valid until',
      open: 'Open the payment request',
      contentHint: 'Keep this transfer content unchanged for the money to be recognised.',
      image: 'QR code',
    },
    unavailable: 'No account can be shown on transfer QR codes (Settings › Modules › Payments).',
  },
  refund: {
    title: 'Refund',
    titleFor: 'Refund for {{domain}}',
    loadError: 'Could not load the refund',
    field: { id: 'Refund identifier', reason: 'Reason', itemStatus: 'Name status', entry: 'Reversal entry', payment: 'Paid back through', created: 'Created' },
    stat: { status: 'Refund status', amount: 'Amount refunded' },
    fromWallet: 'Credited back to the customer wallet',
    entry: 'How the money moved back',
    noEntry: 'No ledger entry recorded for this refund yet.',
    creditNotes: 'Credit notes issued',
    noCreditNotes: 'No credit note issued for this refund.',
  },
  invoices: {
    title: 'Invoices',
    subtitle: 'Invoices and credit notes issued to customers, numbered without gaps per series.',
    limitTitle: 'E-invoices are issued through a module',
    limit: 'Each invoice gets one e-invoice, issued through the provider switched on in Settings › Modules › E-invoice (automatically or by staff, per the settings). Open an invoice to see its number, lookup code and tax authority code; adjusting, replacing and cancelling need a second person\'s approval.',
    documentFilter: 'Document',
    seriesFilter: 'Series',
    seriesPlaceholder: 'e.g. INV',
    loadError: 'Could not load invoices',
    empty: 'No invoices match',
    col: { number: 'Number', document: 'Document', customer: 'Customer', order: 'Order', subtotal: 'Subtotal', tax: 'Tax', total: 'Total', issued: 'Issued' },
  },
  invoice: {
    title: 'Invoice',
    loadError: 'Could not load the invoice',
    field: { document: 'Document', number: 'Number', credits: 'Credits invoice' },
    immutable: 'An issued document is never changed; a correction is a credit note.',
    lines: 'Lines',
    lineCol: { description: 'Description', qty: 'Qty', unitPrice: 'Unit price', amount: 'Amount', tax: 'Tax', taxAmount: 'Tax amount' },
    credit: '(credit)',
    creditedInvoice: 'Credited invoice',
    creditNotes: 'Credit notes',
    buyer: {
      title: 'Buyer',
      description: 'As the customer account held it when the document was issued; later changes to the account do not change it.',
      name: 'Buyer name',
      taxCode: 'Tax code',
      identityNumber: 'Identity number',
      address: 'Address',
      emails: 'Invoice e-mails',
      none: 'Issued before buyer details were kept with invoices.',
    },
  },
  einvoice: {
    title: 'E-invoice',
    description: 'Issued through {{provider}}',
    mode: {
      title: 'Issuing:',
      delivery: 'To the buyer:',
      unreadable: 'the core settings cannot be read (nothing is issued until they are fixed)',
      help: {
        AUTOMATIC: 'Every invoice issued since these settings came into force gets its e-invoice from the system (within about a minute).',
        DAILY: 'The daily 17:00 run creates and signs the e-invoices of the statements issued before 17:00, oldest first, dated the statement\'s day; nothing is sent outside the run.',
        MANUAL: 'E-invoice drafts wait for staff to press "Issue"; the system issues none by itself.',
      },
    },
    draftHelp: {
      AUTOMATIC: 'Not sent to the provider yet. The invoice was issued under automatic issuing: the system issues it at its next run (or press "Issue" now).',
      DAILY: 'Waits for the 17:00 run: the system creates and signs it at the provider, dated the statement\'s day (adjustments and replacements: the day of the run). A customer who needs the buyer details corrected says so before 17:00; once corrected, press "Take the buyer again".',
      MANUAL: 'Not sent to the provider yet. Press "Issue": the system does not issue this one by itself, even if the settings switch to automatic.',
    },
    heldUntil: 'Held because the buyer details were just corrected: it goes in the run of {{at}}, still dated the statement\'s day.',
    loadError: 'Could not load the e-invoices',
    none: 'This invoice has no e-invoice yet.',
    creditNote: 'A credit note has no e-invoice of its own: its invoice\'s e-invoice is adjusted down with it (a second person approves).',
    noProviderTitle: 'No e-invoice provider is switched on',
    noProvider: 'No connection of the E-invoice branch is on, so nothing can be issued; drafts wait until one is.',
    settingsLink: 'Settings › Modules › E-invoice',
    settingsHint: 'The provider, template, series and how invoices are issued (the 17:00 run, automatically or by staff) are in',
    carries: 'carries the credit note',
    taxCode: 'Tax code',
    identity: 'Identity number',
    checkedAt: 'read at {{at}}',
    system: 'System',
    providerCode: 'provider code {{code}}',
    people: 'Asked by {{asker}} · Approved by {{approver}}',
    history: 'History ({{count}})',
    cancellation: 'Cancellation:',
    openCreditNotes: 'Credit notes not on an e-invoice yet ({{count}}):',
    withdrawHelp: 'Drop an adjusting or replacing invoice that was never issued. The original stays as it is; another correction may be asked for.',
    close: 'Close',
    field: {
      number: 'Series · Number',
      template: 'Template',
      issuedAt: 'Issued',
      lookupCode: 'Lookup code',
      taxAuthorityCode: 'Tax authority code',
      authority: 'Tax authority',
      buyer: 'Buyer',
      attempts: 'Attempts',
      provider: 'Connection',
      reason: 'Reason',
      content: 'Corrected content',
      people: 'Asked and approved by',
      delivery: 'Sent to the buyer',
    },
    stateHelp: {
      DRAFT: 'Not sent to the provider yet. Press "Issue" (or wait for automatic issuing, per the settings).',
      ISSUING: 'The provider is being called. If no answer comes, the key is looked up to learn whether it was issued.',
      ISSUED: 'Issued. The buyer looks it up with the lookup code on the provider\'s page.',
      FAILED: 'Not issued (nothing was issued under this key). Fix the cause and send it again.',
      NEEDS_RECONCILIATION: 'Unknown whether the provider issued it. The key is looked up before anything else: never issued twice.',
      REPLACED: 'Replaced by the replacing invoice below.',
      CANCELLED: 'Cancelled at the provider.',
      WITHDRAWN: 'Dropped, never issued.',
    },
    why: {
      FAILED: 'Why it was not issued',
      NEEDS_RECONCILIATION: 'Waiting for the key to be looked up',
      other: 'Needs attention',
    },
    problem: {
      CURRENCY_NOT_SUPPORTED: 'E-invoices are issued in VND only.',
      NO_LINES: 'The document has no lines.',
      LINE_NAME: 'A line has no name (or one too long).',
      LINE_QUANTITY: 'A line\'s quantity is not valid.',
      LINE_AMOUNT: 'A line\'s amount is not a whole number of dong or does not match its price.',
      LINE_VAT_UNKNOWN: 'A line has no tax category (VAT or not taxable).',
      LINE_VAT_RATE: 'A line\'s tax rate is not supported.',
      LINE_VAT_AMOUNT: 'A line\'s tax does not match its rate.',
      TOTALS_MISMATCH: 'The totals do not match the lines.',
      TOTAL_IN_WORDS: 'The total cannot be written in words.',
      BUYER_NAME_MISSING: 'The buyer has no name.',
      BUYER_TOO_LONG: 'The buyer\'s name or address is too long.',
      BUYER_TAX_CODE_MISSING: 'The organisation has no tax code: update the customer\'s invoice details, then press "Take the buyer again".',
      BUYER_TAX_CODE_INVALID: 'The buyer\'s tax code is not valid.',
      BUYER_IDENTITY_INVALID: 'The buyer\'s identity number is not valid (12-digit citizen ID).',
      BUYER_IDENTITY_UNREADABLE: 'The buyer\'s identity number cannot be decrypted (key or context).',
      BUYER_ADDRESS_MISSING: 'The buyer has no address.',
      PAYMENT_METHOD: 'The payment method in the settings is not valid.',
      NOTE_MISSING: 'An information adjustment needs the corrected content.',
    },
    refusal: {
      NOT_FOUND: 'The provider cannot find the invoice.',
      ALREADY_EXISTS: 'The invoice exists at the provider already.',
      UNAUTHORIZED: 'The provider connection\'s account or password is wrong.',
      IP_NOT_ALLOWED: 'The provider does not let this server connect (IP).',
      INVALID_REQUEST: 'The provider refused the invoice\'s content.',
      CONFLICT: 'The provider reported a conflict with its data.',
      QUOTA_EXCEEDED: 'The provider\'s quota is used up.',
      INSUFFICIENT_FUNDS: 'The account at the provider has no balance left.',
      RATE_LIMITED: 'The provider was called too fast; it is tried again.',
      MAINTENANCE: 'The provider is under maintenance.',
      UNSUPPORTED: 'The provider does not support this.',
      BUYER_TAX_CODE_INVALID: 'The provider refused the buyer\'s tax code.',
      TEMPLATE_NOT_FOUND: 'The template or series is not registered for the company at the provider.',
      DUPLICATE_KEY: 'The key was used at the provider already: the key is looked up.',
      NOT_ISSUABLE: 'The provider cannot issue an invoice with this content.',
      TAX_AUTHORITY_REJECTED: 'The tax authority refused the invoice.',
      NUMBERS_EXHAUSTED: 'The series has no invoice numbers left.',
      CERTIFICATE_EXPIRED: 'The provider\'s signing certificate expired.',
      DATE_BEFORE_LAST_ISSUED: 'The invoice date is before the last invoice issued.',
      ALREADY_PROCESSED: 'The invoice was adjusted, replaced or cancelled at the provider already.',
      STATE_NOT_ALLOWED: 'The invoice\'s state at the provider does not allow this.',
      PROVIDER_FAILURE: 'The provider reported an error on its side.',
      NUMBERING_FAILED: 'The provider could not give an invoice number.',
      UNDOCUMENTED: 'The provider answered with a code its documents do not list.',
      TAX_AUTHORITY_PENDING: 'The tax authority has not coded the invoice yet.',
      NO_ANSWER: 'Every attempt went unanswered and the provider has no invoice under the key: check the provider, then send again.',
      DOCUMENT_INVALID: 'The document lacks what an e-invoice needs.',
      other: 'The provider refused ({{code}}).',
    },
    reconcile: {
      UNKNOWN: 'No answer came from the provider.',
      DUPLICATE_KEY: 'The provider said the key was used.',
      PROVIDER_FAILURE: 'The provider reported an error after receiving the request.',
      NUMBERING_FAILED: 'The provider reported a numbering error after receiving the request.',
      STALE: 'The previous call was interrupted.',
    },
    action: {
      CREATE: 'Create the e-invoice',
      ISSUE: 'Issue',
      RETRY: 'Send again',
      RECONCILE: 'Look the key up',
      REFRESH_BUYER: 'Take the buyer again',
      STATUS: 'Read the tax authority\'s answer',
      DOWNLOAD: 'Download',
      DOWNLOAD_PDF: 'Download PDF',
      DOWNLOAD_XML: 'Download XML',
      ADJUST_DECREASE: 'Adjust down',
      ADJUST_INCREASE: 'Adjust up',
      ADJUST_INFORMATION: 'Adjust information',
      REPLACE: 'Replace',
      CANCEL: 'Cancel the invoice',
      WITHDRAW: 'Drop the unissued invoice',
    },
    change: {
      reason: 'Reason (on the invoice and in the log)',
      creditNote: 'The credit note the adjusting invoice carries',
      before: 'What the invoice says',
      beforeHint: 'Optional',
      after: 'What it should say',
      buyerFromAccount: 'The buyer is taken from the customer\'s current invoice details; if they change before the approval is used, ask again.',
      fourEyes: 'Another person who manages e-invoices approves before it is done.',
      submit: 'Send for approval',
      requestedTitle: 'Request sent',
      requested: 'Another person approves it on the page',
      approvals: 'Approvals',
      help: {
        ADJUST_DECREASE: 'Issue an invoice adjusting down that carries the lines of an issued credit note.',
        ADJUST_INFORMATION: 'Issue an invoice adjusting information (no amounts), e.g. the buyer\'s address or name.',
        REPLACE: 'Issue a replacing invoice with the same lines and the buyer as the customer\'s details are now; this invoice becomes "replaced".',
        CANCEL: 'Cancel the invoice at the provider. Only for an invoice made by mistake.',
      },
    },
    pending: {
      title: 'Waiting requests',
      expires: 'expires {{at}}',
      asked: 'Asked by {{name}}, {{at}}',
      approved: 'Approved by {{name}}',
      execute: 'Carry out',
      waiting: 'Waiting for a second person to approve it on the page',
    },
    event: {
      CREATED: 'Draft e-invoice created',
      BUYER_REFRESHED: 'Buyer taken again',
      BUILD_FAILED: 'Could not be built (missing details)',
      ISSUE_SENT: 'Sent for issuing',
      ISSUED: 'Issued',
      NOT_SENT: 'Not sent, will retry',
      NOT_SENT_EXHAUSTED: 'Retries used up, not sent',
      REFUSED: 'Refused by the provider',
      REFUSED_UNSETTLED: 'Provider error, the key is looked up',
      UNKNOWN: 'Outcome unknown, the key is looked up',
      STALE: 'Call interrupted, the key is looked up',
      KEY_NOT_FOUND: 'Lookup: the provider has no invoice under the key',
      RECONCILED_PRESENT: 'Lookup: it was issued',
      RECONCILED_ABSENT: 'Lookup: not issued, sent again with the same key',
      RECONCILE_FAILED: 'Lookup failed',
      ATTENTION: 'Needs staff attention',
      STATUS: 'Tax authority\'s answer',
      DELIVERED: 'Sent to the buyer',
      DELIVERY_FAILED: 'Sending to the buyer failed',
      DELIVERY_BY_PROVIDER: 'The provider e-mails the buyer itself',
      DELIVERY_SKIPPED: 'Not sent to the buyer',
      MARKED_PAID: 'Marked as paid',
      MARK_PAID_FAILED: 'Marking as paid failed',
      CHANGE_PROPOSED: 'Correction or cancellation asked for',
      CHANGE_APPROVED: 'Request approved and carried out',
      CHANGE_WITHDRAWN: 'Request dropped',
      ADJUSTED: 'Adjusted',
      REPLACED: 'Replaced',
      WITHDRAWN: 'Dropped',
      CANCEL_SENT: 'Cancellation sent',
      CANCEL_CHECK: 'Cancellation checked again',
      CANCEL_DONE: 'Cancelled at the provider',
      CANCEL_REFUSED: 'Cancellation refused by the provider',
      CANCEL_NOT_SENT: 'Cancellation not sent, will retry',
      CANCEL_NOT_SENT_EXHAUSTED: 'Retries used up, cancellation not sent',
      CANCEL_UNKNOWN: 'Cancellation outcome unknown, will check again',
      CANCELLED: 'Cancelled',
    },
  },
  pricing: {
    title: 'Pricing',
    subtitle: 'What each name costs us and what we sell it for. Prices are never overwritten: a new version closes the previous one.',
    validity: 'Validity',
    inForce: 'In force',
    withHistory: 'With history',
    loadError: 'Could not load pricing',
    stat: { registryPrices: 'Registry prices', priceBooks: 'Price books', fxRates: 'Exchange rates', taxRates: 'Tax rates' },
    registryCost: 'Registry cost',
    noRegistryPrices: 'No registry prices recorded.',
    registryCol: { tld: 'TLD', registry: 'Registry', operation: 'Operation', years: 'Years', cost: 'Cost', version: 'Version', validity: 'Validity' },
    priceBooks: 'Price books',
    noPriceBooks: 'No price books.',
    bookTier: 'tier {{tier}}',
    bookCustomer: 'customer {{name}}',
    openBook: 'Open price book',
    noRules: 'No rules.',
    noRulesInForce: 'No rules in force.',
    forTld: 'Prices of .{{zone}}',
    allTldsPrices: 'All TLD prices',
    ruleCol: { operation: 'Operation', tld: 'TLD', years: 'Years', rule: 'Rule', value: 'Value', bounds: 'Bounds', priority: 'Priority', validity: 'Validity' },
    allTlds: 'all',
    perYear: 'per year',
    min: 'min',
    max: 'max',
    fxRates: 'Exchange rates',
    noFx: 'No exchange rates recorded.',
    fxCol: { pair: 'Pair', rate: 'Rate', source: 'Taken from', effective: 'Effective' },
    taxRates: 'Tax rates',
    noTax: 'No VAT rate recorded: products cannot be quoted or put on sale until one is.',
    taxDefault: 'Default',
    taxDefaultIs: 'TLD and add-on prices that name no tax code are taxed at {{code}}.',
    taxNoDefault: 'No default tax code: TLD and add-on prices that name none cannot be quoted.',
    taxCol: { jurisdiction: 'Jurisdiction', code: 'Code', rate: 'Rate', source: 'Source', validity: 'Validity' },
    window: { SCHEDULED: 'Scheduled', CLOSED: 'Closed', IN_FORCE: 'In force' },
    from: 'from {{at}}',
    until: 'until {{at}}',
    open: 'open',
  },
  priceBook: {
    title: 'Price book',
    loadError: 'Could not load the price book',
    field: { code: 'Code', appliesTo: 'Applies to', tier: 'Tier', customer: 'Customer', created: 'Created' },
    stat: { rules: 'Rules', quotes: 'Quotes priced', lastQuoted: 'Last used' },
    never: 'Never used',
    rules: 'Selling price per TLD',
    allRules: 'Closed windows are shown too: a rule is superseded, never edited.',
    noRules: 'This book has no rules, so nothing can be priced from it.',
  },
  promotions: {
    title: 'Promotions',
    subtitle: 'Discount codes customers can use, when they run and how often they have been redeemed.',
    stateFilter: 'State',
    loadError: 'Could not load promotions',
    empty: 'No promotions match',
    col: { code: 'Code', discount: 'Discount', appliesTo: 'Applies to', state: 'State', window: 'Window', redemptions: 'Redemptions' },
    anyOperation: 'Any operation',
    allTlds: 'all TLDs',
    filterBy: 'Show {{state}} promotions',
  },
  promotion: {
    title: 'Promotion',
    loadError: 'Could not load the promotion',
    field: { code: 'Code', created: 'Created', starts: 'Starts', ends: 'Ends', limit: 'Redemption limit', noLimit: 'No limit' },
    stat: { state: 'State', discount: 'Discount', redemptions: 'Redemptions', left: '{{count}} left' },
    usage: 'Quotes that used this code',
    usageNote: 'The discount is part of each quote, so what a customer was charged stays provable after the code ends.',
    noUsage: 'This code has not been used in a quote yet.',
    usageCol: { domain: 'Name', customer: 'Customer', discount: 'Discount', total: 'Quote total', order: 'Order', created: 'Quoted' },
    notOrdered: 'Not ordered',
  },
  enum: {
    orderStatus: { PLACED: 'Placed', FUNDS_RESERVED: 'Funds reserved', FULFILLING: 'Fulfilling', COMPLETED: 'Completed', PARTIALLY_COMPLETED: 'Partially completed', FAILED: 'Failed', CANCELLED: 'Cancelled' },
    orderItemStatus: { PENDING: 'Pending', FUNDS_RESERVED: 'Funds reserved', FULFILLING: 'Fulfilling', FULFILLED: 'Fulfilled', FAILED: 'Failed', REFUNDED: 'Refunded', CANCELLED: 'Cancelled' },
    holdStatus: { ACTIVE: 'Active', CAPTURED: 'Captured', RELEASED: 'Released', EXPIRED: 'Expired' },
    paymentStatus: { INITIATED: 'Initiated', PENDING: 'Pending', SUCCEEDED: 'Succeeded', FAILED: 'Failed', UNKNOWN: 'Unknown — reconcile', CANCELLED: 'Cancelled' },
    refundStatus: { COMPLETED: 'Completed', PENDING_PROVIDER: 'Pending provider', FAILED: 'Failed' },
    sourceRefundStatus: { PENDING_PROVIDER: 'With the gateway', COMPLETED: 'Refunded', FAILED: 'Failed (returned to the wallet)' },
    attemptStatus: {
      CREATED: 'Created',
      PENDING: 'At the gateway',
      REQUIRES_ACTION: 'Waiting for the customer',
      AUTHORIZED: 'Authorized, not captured',
      CAPTURED: 'Paid',
      FAILED: 'Failed',
      CANCELLED: 'Cancelled',
      EXPIRED: 'Expired',
      UNKNOWN: 'Unknown — being queried',
    },
    docType: { INVOICE: 'Invoice', CREDIT_NOTE: 'Credit note' },
    einvoiceState: { DRAFT: 'Draft', ISSUING: 'Issuing', ISSUED: 'Issued', FAILED: 'Not issued', NEEDS_RECONCILIATION: 'To look up', REPLACED: 'Replaced', CANCELLED: 'Cancelled', WITHDRAWN: 'Dropped' },
    einvoiceKind: { ORIGINAL: 'Original', ADJUSTMENT: 'Adjusting invoice', REPLACEMENT: 'Replacing invoice' },
    einvoiceAdjust: { INCREASE: 'Adjusted up', DECREASE: 'Adjusted down', INFORMATION: 'Information adjusted' },
    einvoiceAuthority: { DRAFT: 'Draft', ISSUED: 'Waiting for the tax authority', SENT_TO_TAX_AUTHORITY: 'Sent to the tax authority', ACCEPTED_BY_TAX_AUTHORITY: 'Coded by the tax authority', REJECTED_BY_TAX_AUTHORITY: 'Refused by the tax authority', ADJUSTED: 'Adjusted', REPLACED: 'Replaced', CANCELLED: 'Cancelled' },
    einvoiceCancel: { QUEUED: 'Waiting', SENDING: 'Sending', DONE: 'Cancelled', REFUSED: 'Refused', UNKNOWN: 'Outcome unknown', FAILED: 'Not sent' },
    einvoiceApproval: { PENDING: 'Waiting for approval', APPROVED: 'Approved' },
    einvoiceMode: { MANUAL: 'Staff press Issue', AUTOMATIC: 'Issued automatically when the invoice is issued', DAILY: 'The daily 17:00 run' },
    einvoiceDelivery: { PROVIDER: 'E-mailed by the provider', SENT: 'Sent by the provider', FAILED: 'Sending failed', SKIPPED: 'Not sent' },
    einvoiceDeliverySetting: { PROVIDER: 'The e-invoice provider e-mails the buyer', NONE: 'Not sent to the buyer' },
    accountStatus: { OPEN: 'Open', FROZEN: 'Frozen', CLOSED: 'Closed' },
    serviceAction: { NEW: 'New service', RENEW: 'Renew', CHANGE_PLAN: 'Change plan', CHANGE_OPTIONS: 'Change options', ADDON: 'Add-on' },
    operation: { REGISTER: 'Register', RENEW: 'Renew', TRANSFER: 'Transfer', RESTORE: 'Restore', REDEMPTION: 'Redemption', ADDON: 'Add-on', BACKORDER: 'Backorder', AUCTION: 'Auction' },
    holdPurpose: { REGISTRATION: 'Registration', RENEWAL: 'Renewal', TRANSFER: 'Transfer', RESTORE: 'Restore', AUCTION: 'Auction', DROPCATCH: 'Drop-catch', OTHER: 'Other' },
    paymentPurpose: { WALLET_TOPUP: 'Wallet top-up', ORDER: 'Order' },
    bankTxFilter: { OPEN: 'To handle', UNMATCHED: 'Not matched', REVIEW: 'For review', SETTLED: 'Settled', IGNORED: 'Set aside' },
    bankTxStatus: { UNMATCHED: 'Not matched', REVIEW: 'For review', SETTLED: 'Settled', IGNORED: 'Set aside' },
    bankTxReason: {
      NO_CODE: 'No code in the content',
      UNKNOWN_CODE: 'Code of no request',
      DUPLICATE_REFERENCE: 'Transaction code recorded before',
      SHARED_ACCOUNT: 'Account reported by two connections',
      AMOUNT_SHORT: 'Less than requested',
      AMOUNT_OVER: 'More than requested',
      REQUEST_EXPIRED: 'After the request lapsed',
      REQUEST_CLOSED: 'Request cancelled',
      ALREADY_PAID: 'Request already paid',
      OTHER_ACCOUNT: 'To another account',
      MATCHED: 'Matched automatically',
      ASSIGNED: 'Assigned by staff',
      CREDITED: 'Credited to the wallet',
      CONFIRMED: 'Confirmed by staff',
      OUTGOING: 'Money out',
      DISMISSED: 'Set aside by staff',
      SETTLEMENT_PAYOUT: 'Gateway settlement',
    },
    txDirection: { IN: 'Money in', OUT: 'Money out' },
    approvalStatus: { PENDING: 'Waiting for approval', APPROVED: 'Approved', REJECTED: 'Rejected', EXECUTED: 'Used', EXPIRED: 'Expired', CANCELLED: 'Cancelled' },
    orderFunding: { FUNDED: 'The order goes on', NOT_WAITING: 'The order was not waiting', INSUFFICIENT: 'The order still waits (wallet short)' },
    bankFeed: { sepay: 'SePay', casso: 'Casso', tinopay: 'TinoPay', vnpay: 'VNPAY', fake: 'Simulated feed', staff: 'Staff (by hand)' },
    accountType: { ASSET: 'Asset', LIABILITY: 'Liability', EQUITY: 'Equity', REVENUE: 'Revenue', EXPENSE: 'Expense' },
    accountKind: {
      WALLET: 'Customer wallet',
      CLEARING: 'Payment provider clearing',
      REVENUE: 'Revenue',
      COST_OF_SALES: 'Cost of sales',
      REGISTRY_PAYABLE: 'Owed to registry',
      REFUNDS_PAYABLE: 'Refunds owed',
      TAX_PAYABLE: 'Tax owed',
      OTHER: 'Ledger account',
    },
    ownerType: { CUSTOMER: 'Customer', REGISTRY: 'Registry', PLATFORM: 'Platform' },
    entryType: { WALLET_TOPUP: 'Wallet top-up', HOLD_CAPTURE: 'Hold capture', REGISTRY_COST_ACCRUAL: 'Registry cost accrual', REVERSAL: 'Reversal', REFUND: 'Refund', WALLET_ADJUSTMENT: 'Balance adjustment', WALLET_PAYOUT: 'Refund to the source' },
    resourceType: { payment: 'Payment', balance_hold: 'Balance hold', order_item: 'Ordered name', journal_entry: 'Journal entry', refund: 'Refund', customer: 'Customer' },
    ruleType: { MARKUP_PERCENT: 'Markup (%)', MARKUP_FIXED: 'Fixed markup', FIXED_PRICE: 'Fixed price' },
    appliesTo: { TIER: 'Tier', CUSTOMER_CONTRACT: 'Customer contract' },
    bookStatus: { DRAFT: 'Draft', ACTIVE: 'Active', RETIRED: 'Retired' },
    promoState: { ACTIVE: 'Active', SCHEDULED: 'Scheduled', EXHAUSTED: 'Exhausted', ENDED: 'Ended' },
    actorType: { CUSTOMER: 'Customer', OPERATOR: 'Operator', SYSTEM: 'System', RESELLER: 'Reseller', EXTERNAL: 'External party' },
  },
  matrix: {
    defaults: 'Default rule',
    defaultsNone: 'No default rule: a TLD without its own price cannot be priced.',
    rule: { FIXED_PRICE: '{{value}}', MARKUP_FIXED: 'Cost + {{value}}', MARKUP_PERCENT: 'Cost + {{value}} %' },
    ruleMin: 'at least {{value}}',
    ruleMax: 'at most {{value}}',
    op: { REGISTER: 'Register', RENEW: 'Renew', TRANSFER: 'Transfer in', RESTORE: 'Restore' },
    oneYear: '1 year',
    tld: 'TLD',
    book: 'Price book',
    search: 'Find a TLD',
    onlySpecific: 'Only TLDs with their own price',
    source: { SPECIFIC: 'own', DEFAULT: 'default', FREE: 'free', NO_COST: 'no cost', NO_RULE: 'no price', NOT_OFFERED: 'not offered', NO_PRICE: 'cannot price' },
    renewalBasis: 'as a renewal',
    empty: 'No TLD matches.',
    note: 'What a customer pays for 1 year, before tax, without the mandatory fees (VNNIC fees, ICANN fee: see the TLD page).',
    loadError: 'Could not load the prices',
    addSpecific: 'Own price for a TLD…',
    pick: 'Own price for a TLD · {{book}}',
    pickTld: 'TLD',
    pickOp: 'Operation',
    choose: 'Choose…',
    more: 'More actions',
    details: 'History & rule details',
    since: 'since {{date}}',
    cell: {
      title: 'Selling price .{{zone}} · {{op}} · {{book}}',
      description: 'A new version starts at the chosen time and closes the one in force; quotes already given keep their price.',
      current: 'Customer pays now (1 year, before tax)',
      how: 'How it is worked out',
      cost: 'Registry cost',
      fromDefault: 'the book\'s default rule',
      fromSpecific: 'this TLD\'s own rule',
      noCost: 'No registry cost for this operation: set it on the TLD page first.',
      noRule: 'No rule applies.',
      notOffered: 'Not offered by the TLD settings.',
      free: 'Free by the TLD settings.',
      choice: 'New price',
      useDefault: 'Use the default rule',
      fixed: 'Fixed price',
      percent: 'Cost + %',
      markup: 'Cost + amount',
      value: 'Value',
      defaultWillApply: 'Then: {{rule}}',
      noDefault: 'The book has no default rule for this operation: the TLD will not be priced.',
      alreadyDefault: 'Already uses the default rule.',
      from: 'Effective from',
      fromHint: 'Empty = now.',
      save: 'Save price',
      saved: 'Saved.',
    },
    grid: {
      title: 'Selling price .{{zone}} · {{book}}',
      description: 'Every operation of the TLD in one go. Operations left on "Keep" do not change; the others are saved together from the chosen time. Quotes already given keep their price.',
      op: 'Operation',
      current: 'Customer pays now (1 year)',
      method: 'New setting',
      preview: 'Customer will pay',
      keep: 'Keep',
      all: 'Apply to every operation',
      apply: 'Apply',
      costLine: 'cost {{cost}}',
      noCost: 'No registry cost',
      noDefault: 'No default rule',
      renewal: 'Priced as a renewal',
      plusRenewal: '+ renewal price',
      save_one: 'Save {{count}} operation',
      save_other: 'Save {{count}} operations',
      nothing: 'No operation changed yet.',
      saved_one: 'Saved the price of {{count}} operation.',
      saved_other: 'Saved prices for {{count}} operations.',
      pickFirst: 'Choose a TLD to price every operation.',
    },
  },
} as const;
